import {
  selectIElementIdToColorMap,
  selectVisibilityMap,
} from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-selectors";
import { PointCloudObject } from "@/object-cache";
import { useMultiCloudPointBudgetManager } from "@/registration-tools/common/rendering/use-multi-cloud-point-budget-manager";
import { usePointCloudMaterials } from "@/registration-tools/common/rendering/use-point-cloud-materials";
import { useAppSelector } from "@/store/store-hooks";
import { EMPTY_ARRAY, GUID } from "@faro-lotv/foundation";
import { RevisionScanEntity } from "@faro-lotv/service-wires";
import { useMemo, type JSX } from "react";
import { Color, Plane } from "three";
import { useDataPreparationOverlayElements } from "../data-preparation-overlay-context";
import { selectScanColoring } from "../store/data-preparation-view-options/data-preparation-view-options-selectors";
import { ScanColoring } from "../store/data-preparation-view-options/data-preparation-view-options-slice";
import { Projection } from "../ui/projection-switch";
import { RevisionScanRenderer } from "./revision-scan-renderer";
import { useScaledPointOpacity } from "./use-scaled-point-opacity";

export type CaptureTreeScansProps = {
  /** The scan entities to show */
  scanEntities: RevisionScanEntity[];

  /** The point cloud objects for the scan entities*/
  pointCloudObjects: PointCloudObject[];

  /** The point clouds to highlight in a hover interaction */
  hoveredPointCloudIds?: GUID[];

  /** The point clouds to highlight in a selection interaction */
  selectedPointCloudIds?: GUID[];

  /** Clipping planes to use for rendering */
  clippingPlanes?: Plane[];

  /** Callback to execute when the scans are placed at their initial position. */
  onInitialPositioning(): void;
};

const MAX_POINTS_GPU_SCAN_BY_COLOR = 4_000_000;
const MAX_POINTS_GPU_ORIGINAL_COLOR = 10_000_000;
/**
 * Clamp the color lightness.
 * The points are blended on white, so high lightness can have low contrast.
 */
const MAX_LIGHTNESS = 0.4;

/** @returns Render the scans from the capture tree at the position of their revisions */
export function RevisionScansRenderer({
  scanEntities,
  pointCloudObjects,
  hoveredPointCloudIds = EMPTY_ARRAY,
  selectedPointCloudIds = EMPTY_ARRAY,
  clippingPlanes,
  onInitialPositioning,
}: CaptureTreeScansProps): JSX.Element {
  const scanColoring = useAppSelector(selectScanColoring);

  const iElementIdToColorMap = useAppSelector(selectIElementIdToColorMap);

  const highlightedIds = useMemo(
    () => [...hoveredPointCloudIds, ...selectedPointCloudIds],
    [hoveredPointCloudIds, selectedPointCloudIds],
  );

  const baseColors = useMemo(
    () =>
      pointCloudObjects.map(
        (pointCloud) => new Color(iElementIdToColorMap[pointCloud.iElement.id]),
      ),
    [iElementIdToColorMap, pointCloudObjects],
  );

  // When using scaled point opacity, too light colors can have poor contrast on the white background
  const highContrastColors = useMemo(() => {
    const tmpHsl = { h: 0, s: 0, l: 0 };

    return baseColors.map((baseColor) => {
      const hsl = baseColor.getHSL(tmpHsl);
      // Clamp lightness to improve contrast when blending on white background
      hsl.l = Math.min(hsl.l, MAX_LIGHTNESS);
      return new Color().setHSL(hsl.h, hsl.s, hsl.l);
    });
  }, [baseColors]);

  const maxPointsInGpu =
    scanColoring === ScanColoring.byScan
      ? MAX_POINTS_GPU_SCAN_BY_COLOR
      : MAX_POINTS_GPU_ORIGINAL_COLOR;

  const { projection } = useDataPreparationOverlayElements();
  // Internal testing found that the scaled point opacity only works well for the 2D orthographic camera mode
  // In original color mode, points are always rendered at full opacity to preserve color fidelity
  const enableScaledPointOpacity =
    projection === Projection.twoDimensional &&
    scanColoring !== ScanColoring.originalColor;
  useScaledPointOpacity(pointCloudObjects, enableScaledPointOpacity);

  const colors = enableScaledPointOpacity ? highContrastColors : baseColors;

  usePointCloudMaterials({
    pointClouds: pointCloudObjects,
    scanColoring,
    highlightedIds,
    clippingPlanes,
    colors,
  });

  const visibilityMap = useAppSelector(selectVisibilityMap);
  const visiblePointCloudObjects = useMemo(
    () =>
      pointCloudObjects.filter(
        (pc) =>
          // The parent ID of the point cloud corresponds to the ID of the scan entity
          pc.iElement.parentId && visibilityMap[pc.iElement.parentId] !== false,
      ),
    [pointCloudObjects, visibilityMap],
  );

  // Only assign point budget for the actually visible point clouds
  useMultiCloudPointBudgetManager(visiblePointCloudObjects, maxPointsInGpu);

  return (
    <group name="point clouds">
      {scanEntities.map((scanEntity) => (
        <RevisionScanRenderer
          key={scanEntity.id}
          scanEntity={scanEntity}
          onInitialPositioning={onInitialPositioning}
        />
      ))}
    </group>
  );
}
